/**
 * Top-level site shell.
 *
 * The happy path (fresh user):
 *   1. Visitor lands on `/` → LandingPage shows an email form.
 *   2. Admin approves; user clicks the magic-link email → `/?claim=<token>`.
 *   3. ClaimHandler mints identity + auth Ed25519 pairs, binds them to
 *      the magic-link token via /auth/claim, returns a session token.
 *   4. PassphrasePrompt(mode='create') asks for a passphrase. The
 *      identity is then sealed under that passphrase and written to
 *      IndexedDB (HIGH-06).
 *   5. If the platform supports WebAuthn PRF, BiometricEnrollPrompt
 *      offers to wrap that passphrase under a biometric credential
 *      so the next unlock can skip typing it.
 *   6. Authenticated state renders the Chat view.
 *
 * Returning user:
 *   1. On load, detect a pre-existing sealed identity by the presence
 *      of the salt record.
 *   2. PassphrasePrompt(mode='unlock'). If a biometric credential is
 *      enrolled, the "Unlock with biometrics" button is shown above
 *      the passphrase input. Either path yields a passphrase which
 *      drives the same unlock flow.
 *   3. loadIdentity(sealedStore) returns the stored identity; signIn
 *      hits /auth/verify and we land on 'signed-in'.
 *
 * If /auth/verify returns 403 (server lost the identity binding — e.g.
 * operator purged the KV record) we punt the user back to 'landing'
 * so they can re-register.
 */
import { PROTOCOL_VERSION, toBase64Url } from '@chat/protocol';
import { useEffect, useState } from 'react';
import { ChatClient } from './client/api.js';
import { type KeyStore } from '@chat/crypto';
import {
  clearBiometric,
  enrollBiometric,
  hasBiometricEnrolled,
  isWebAuthnPrfAvailable,
  unlockWithBiometric,
} from './client/biometric-unlock.js';
import { IndexedDbKeyStore } from './client/indexeddb-keystore.js';
import { loadIdentity, saveIdentity, type Identity } from './client/identity.js';
import { openSealedStore, readSalt, unlockSealedStore } from './client/sealed-store.js';
import { exportBackup, importBackup, parseBackupBundle } from './client/backup.js';
import { BiometricEnrollPrompt } from './components/BiometricEnrollPrompt.js';
import { Chat } from './components/Chat.js';
import { ClaimHandler } from './components/ClaimHandler.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { InstallPrompt } from './components/InstallPrompt.js';
import { LandingPage } from './components/LandingPage.js';
import { PassphrasePrompt } from './components/PassphrasePrompt.js';

const DEFAULT_API_BASE = (globalThis as { __CHAT_API_BASE__?: string }).__CHAT_API_BASE__ ?? '';
const IDENTITY_PROBE_KEY = 'chat:identity:v2';

function buildClient(): ChatClient {
  return new ChatClient(DEFAULT_API_BASE || window.location.origin);
}

function readClaimToken(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  const token = params.get('claim');
  return token ?? undefined;
}

type Phase =
  | 'boot'
  | 'unlock'
  | 'claim'
  | 'set-passphrase'
  | 'offer-biometric'
  | 'landing'
  | 'signed-in';

export interface AppProps {
  readonly client?: ChatClient;
  readonly store?: IndexedDbKeyStore;
  readonly claimToken?: string;
}

export function App({ client: injectedClient, store: injectedStore, claimToken }: AppProps = {}) {
  const [rawStore] = useState(() => injectedStore ?? new IndexedDbKeyStore());
  const [client] = useState<ChatClient>(() => injectedClient ?? buildClient());
  const [sealedStore, setSealedStore] = useState<KeyStore | undefined>();
  const [identity, setIdentity] = useState<Identity | undefined>();
  const [token, setToken] = useState<string | undefined>();
  const [phase, setPhase] = useState<Phase>('boot');
  const [error, setError] = useState<string | undefined>();
  const [pendingEmail, setPendingEmail] = useState<string | undefined>();
  const [activeClaimToken] = useState<string | undefined>(() => claimToken ?? readClaimToken());
  const [pendingPassphrase, setPendingPassphrase] = useState<string | undefined>();
  const [biometricEnrolled, setBiometricEnrolled] = useState(false);

  // Boot: pick the initial phase. Claim token in URL → go through
  // claim flow (ends at set-passphrase → offer-biometric). Pre-
  // existing salt → unlock flow. Neither → landing.
  useEffect(() => {
    let cancelled = false;
    if (activeClaimToken) {
      setPhase('claim');
      return () => {
        cancelled = true;
      };
    }
    void (async () => {
      try {
        const [salt, enrolled] = await Promise.all([
          readSalt(rawStore),
          hasBiometricEnrolled(rawStore),
        ]);
        if (cancelled) return;
        setBiometricEnrolled(enrolled);
        setPhase(salt ? 'unlock' : 'landing');
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'boot failed');
          setPhase('landing');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rawStore, activeClaimToken]);

  async function completeUnlock(passphrase: string): Promise<void> {
    const unlocked = await unlockSealedStore(rawStore, passphrase, IDENTITY_PROBE_KEY);
    const loaded = await loadIdentity(unlocked);
    if (!loaded) throw new Error('no identity stored — re-register');
    const sessionToken = await client.signIn(loaded);
    setSealedStore(unlocked);
    setIdentity(loaded);
    setToken(sessionToken);
    setPhase('signed-in');
  }

  async function handleUnlock(passphrase: string): Promise<void> {
    await completeUnlock(passphrase);
  }

  async function handleBiometricUnlock(): Promise<void> {
    const passphrase = await unlockWithBiometric(rawStore);
    await completeUnlock(passphrase);
  }

  async function handleSetPassphrase(passphrase: string): Promise<void> {
    if (!identity) throw new Error('identity missing — refresh and try again');
    const { sealed } = await openSealedStore(rawStore, passphrase, { createIfMissing: true });
    await saveIdentity(sealed, identity);
    setSealedStore(sealed);
    // LOW-V3-02: a previous enrollment on this device might still sit
    // at chat:biometric:v1 (e.g. the user cleared identity + salt
    // without clearing everything). Wipe it so the re-enrollment step
    // below starts from a clean slate and we don't end up with a
    // stale wrap from a different passphrase.
    await clearBiometric(rawStore);
    setBiometricEnrolled(false);
    // Hold the passphrase briefly so the biometric-enrollment step
    // can wrap it under a WebAuthn credential if the user opts in.
    // If WebAuthn PRF isn't available we skip straight to signed-in.
    const prfOk = await isWebAuthnPrfAvailable();
    if (prfOk) {
      setPendingPassphrase(passphrase);
      setPhase('offer-biometric');
    } else {
      setPhase('signed-in');
    }
  }

  async function handleEnrollBiometric(): Promise<void> {
    if (!pendingPassphrase) throw new Error('passphrase not available');
    await enrollBiometric(rawStore, pendingPassphrase);
    setPendingPassphrase(undefined);
    setBiometricEnrolled(true);
    setPhase('signed-in');
  }

  function handleSkipBiometric(): void {
    setPendingPassphrase(undefined);
    setPhase('signed-in');
  }

  async function handleImportBackup(file: File): Promise<void> {
    // Refuse to clobber an existing identity — catches the
    // "I meant to create a new account but clicked restore"
    // footgun. If there's already a salt the user should wipe
    // their browser state first.
    const existingSalt = await readSalt(rawStore);
    if (existingSalt) {
      throw new Error(
        'this device already has an identity — clear site data first to restore a backup',
      );
    }
    const text = await file.text();
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new Error('backup file is not valid JSON');
    }
    const bundle = parseBackupBundle(parsed);
    await importBackup(rawStore, bundle);
    // The raw store now holds the salt + sealed entries. Transition
    // to the unlock flow so the user can enter their passphrase.
    setPhase('unlock');
  }

  return (
    <>
      {phase === 'boot' && <p data-testid="booting">loading…</p>}
      {error && <p data-testid="error">error: {error}</p>}

      {phase === 'unlock' && (
        <PassphrasePrompt
          mode="unlock"
          onSubmit={handleUnlock}
          {...(biometricEnrolled ? { onBiometricUnlock: handleBiometricUnlock } : {})}
        />
      )}

      {phase === 'claim' && activeClaimToken && (
        <ClaimHandler
          client={client}
          token={activeClaimToken}
          onClaimed={({ identity: fresh, sessionToken }) => {
            setIdentity(fresh);
            setToken(sessionToken);
            setPhase('set-passphrase');
          }}
        />
      )}

      {phase === 'set-passphrase' && (
        <PassphrasePrompt mode="create" onSubmit={handleSetPassphrase} />
      )}

      {phase === 'offer-biometric' && (
        <BiometricEnrollPrompt onEnroll={handleEnrollBiometric} onSkip={handleSkipBiometric} />
      )}

      {phase === 'landing' && (
        <>
          {pendingEmail ? (
            <main data-testid="pending-email">
              <h2>thanks — we've emailed the admin</h2>
              <p>
                Once <code>{pendingEmail}</code> is approved you'll get a sign-in link in your
                inbox. You can close this tab.
              </p>
            </main>
          ) : (
            <LandingPage
              client={client}
              onSubmitted={(email) => {
                setPendingEmail(email);
              }}
              onImportBackup={handleImportBackup}
            />
          )}
        </>
      )}

      {phase === 'signed-in' && identity && token && sealedStore && (
        <div className="app-shell" data-testid="app-shell">
          <header className="app-topbar">
            <span className="brand" aria-label="Gamez">
              <span className="brand-knight" aria-hidden="true">
                ♞
              </span>
              Gamez
            </span>
            <span data-testid="protocol-version" className="proto">
              protocol v{PROTOCOL_VERSION}
            </span>
            <span className="spacer" />
            <span data-testid="public-key" className="identity-tag">
              {toBase64Url(identity.identityPublicKey).slice(0, 12)}…
            </span>
            {/* Exposed for E2E so a second browser can look up
                whoever just claimed an identity on this page. Hidden
                visually — the public-key tag above is the human-
                facing identity hint. */}
            <span
              data-testid="user-id"
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1 }}
              aria-hidden="true"
            >
              {identity.userId}
            </span>
            <InstallPrompt />
          </header>
          <ErrorBoundary>
            <Chat
              identity={identity}
              client={client}
              store={sealedStore}
              token={token}
              onExportBackup={() => exportBackup(rawStore)}
            />
          </ErrorBoundary>
        </div>
      )}
    </>
  );
}
